/* eslint-disable react-refresh/only-export-components */
import React, {
  createContext,
  useContext,
  useRef,
  useCallback,
  useEffect,
  useSyncExternalStore,
} from 'react';
import type { CellChange } from '../components/handsontable/tracking';

export interface ToolbarState {
  onReload?: () => void | Promise<void>;
  onSave?: () => void | Promise<void>;
  loading?: boolean;
  saving?: boolean;
  changesCount?: number;
  filters?: React.ReactNode;
  additionalControls?: React.ReactNode;
  reloadDisabled?: boolean;
  reloadDisabledReason?: string;
  saveDisabled?: boolean;
  saveDisabledReason?: string;
  /** RBAC ресурс для проверки прав */
  rbacResource?: string;
  rbacEnabled?: boolean;
  showRbacBadge?: boolean;
}

/** Действия таблицы, регистрируемые из TrackedTable */
export interface TableActionsState {
  onUndo?: () => void;
  onRedo?: () => void;
  onClear?: () => void | Promise<void>;
  changedCells?: CellChange[];
}

/** Объединённое состояние тулбара (страница + таблицы) */
export type MergedToolbarState = ToolbarState & TableActionsState;

interface ToolbarContextValue {
  register: (state: ToolbarState) => void;
  unregister: () => void;
  registerTableActions: (tableId: string, actions: TableActionsState) => void;
  unregisterTableActions: (tableId: string) => void;
  setReloading: (value: boolean) => void;
  subscribe: (onStoreChange: () => void) => () => void;
  getSnapshot: () => MergedToolbarState;
  getReloadingSnapshot: () => boolean;
}

const EMPTY_STATE: ToolbarState = {};

function shallowEqualMerged(a: MergedToolbarState, b: MergedToolbarState): boolean {
  const aKeys = Object.keys(a) as (keyof MergedToolbarState)[];
  const bKeys = Object.keys(b) as (keyof MergedToolbarState)[];
  if (aKeys.length !== bKeys.length) return false;
  return aKeys.every((key) => Object.is(a[key], b[key]));
}

const ToolbarContext = createContext<ToolbarContextValue | undefined>(undefined);

/**
 * Провайдер тулбара.
 *
 * Ключевой принцип: НЕ использует useState для хранения состояния.
 * Вместо этого — ref + подписка (useSyncExternalStore pattern).
 *
 * Это гарантирует, что:
 * - Контекстное значение СТАБИЛЬНО (не меняется) → страницы,
 *   вызывающие useRegisterToolbar, НЕ перерисовываются от контекста.
 * - Только компоненты-потребители (SubHeaderStages, FloatingActionBar),
 *   подписанные через useSyncExternalStore, получают обновления.
 */
export const ToolbarProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const stateRef = useRef<ToolbarState>(EMPTY_STATE);
  const tableActionsMapRef = useRef(new Map<string, TableActionsState>());
  const mergedRef = useRef<MergedToolbarState>(EMPTY_STATE);
  const listenersRef = useRef(new Set<() => void>());
  const reloadingRef = useRef(false);
  const prevChangedCellsRef = useRef<CellChange[] | undefined>(undefined);

  // Стабильные ссылки на объединённые undo/redo/clear — читают текущую карту
  // лениво, а не пересоздаются при каждом rebuild. Если бы они пересоздавались,
  // getSnapshot() всегда возвращал бы «изменившийся» объект и useSyncExternalStore
  // бесконечно перерисовывал бы подписчиков (Maximum update depth exceeded).
  const combinedUndoRef = useRef(() => {
    for (const actions of tableActionsMapRef.current.values()) actions.onUndo?.();
  });
  const combinedRedoRef = useRef(() => {
    for (const actions of tableActionsMapRef.current.values()) actions.onRedo?.();
  });
  const combinedClearRef = useRef(async () => {
    for (const actions of tableActionsMapRef.current.values()) {
      await actions.onClear?.();
    }
  });

  const subscribe = useCallback((cb: () => void) => {
    listenersRef.current.add(cb);
    return () => {
      listenersRef.current.delete(cb);
    };
  }, []);

  const rebuildMerged = useCallback(() => {
    const map = tableActionsMapRef.current;
    let hasUndo = false;
    let hasRedo = false;
    let hasClear = false;
    const allCells: CellChange[] = [];
    for (const actions of map.values()) {
      if (actions.onUndo) hasUndo = true;
      if (actions.onRedo) hasRedo = true;
      if (actions.onClear) hasClear = true;
      if (actions.changedCells) allCells.push(...actions.changedCells);
    }

    // Переиспользуем предыдущую ссылку массива, если содержимое не изменилось.
    const prevCells = prevChangedCellsRef.current;
    const sameCells = !!prevCells
      && prevCells.length === allCells.length
      && prevCells.every((c, i) => c === allCells[i]);
    const changedCells = allCells.length > 0 ? (sameCells ? prevCells : allCells) : undefined;
    prevChangedCellsRef.current = changedCells;

    const state = stateRef.current;
    const next: MergedToolbarState = {
      ...state,
      onUndo: hasUndo ? combinedUndoRef.current : undefined,
      onRedo: hasRedo ? combinedRedoRef.current : undefined,
      onClear: hasClear ? combinedClearRef.current : undefined,
      changedCells,
      loading: state.loading || reloadingRef.current,
    };

    // Сохраняем прежнюю ссылку целиком, если ничего не изменилось —
    // иначе getSnapshot() каждый раз «меняется», и любой useSyncExternalStore
    // подписчик перерисовывается впустую (или зацикливается).
    if (!shallowEqualMerged(mergedRef.current, next)) {
      mergedRef.current = next;
    }
  }, []);

  const getSnapshot = useCallback(() => mergedRef.current, []);
  const getReloadingSnapshot = useCallback(() => reloadingRef.current, []);

  const emitChange = useCallback(() => {
    rebuildMerged();
    for (const cb of listenersRef.current) cb();
  }, [rebuildMerged]);

  const register = useCallback(
    (newState: ToolbarState) => {
      stateRef.current = newState;
      emitChange();
    },
    [emitChange],
  );

  const unregister = useCallback(() => {
    stateRef.current = EMPTY_STATE;
    emitChange();
  }, [emitChange]);

  const registerTableActions = useCallback(
    (tableId: string, actions: TableActionsState) => {
      // Пропускаем пустые регистрации (enableTracking=false или enableLocalToolbar=false)
      const hasActions = actions.onUndo || actions.onRedo || actions.onClear ||
        (actions.changedCells && actions.changedCells.length > 0);
      if (hasActions) {
        tableActionsMapRef.current.set(tableId, actions);
      } else {
        tableActionsMapRef.current.delete(tableId);
      }
      emitChange();
    },
    [emitChange],
  );

  const unregisterTableActions = useCallback((tableId: string) => {
    tableActionsMapRef.current.delete(tableId);
    emitChange();
  }, [emitChange]);

  const setReloading = useCallback((value: boolean) => {
    reloadingRef.current = value;
    emitChange();
  }, [emitChange]);

  // Значение контекста СТАБИЛЬНО — все функции мемоизированы, зависимостей нет.
  // useContext(ToolbarContext) в useRegisterToolbar НЕ вызовет ре-рендер страницы.
  const value = React.useMemo<ToolbarContextValue>(
    () => ({ register, unregister, registerTableActions, unregisterTableActions, setReloading, subscribe, getSnapshot, getReloadingSnapshot }),
    [register, unregister, registerTableActions, unregisterTableActions, setReloading, subscribe, getSnapshot, getReloadingSnapshot],
  );

  return (
    <ToolbarContext.Provider value={value}>
      {children}
    </ToolbarContext.Provider>
  );
};

/**
 * Регистрирует toolbar-состояние страницы в контексте.
 * При размонтировании автоматически очищает.
 *
 * Заменяет `<UnifiedTableToolbar />` — вместо рендера JSX-компонента
 * страница вызывает этот хук с теми же данными.
 *
 * Безопасен от infinite loops: контекст стабилен (ref-based),
 * поэтому useContext НЕ вызывает ре-рендер страницы.
 */
export function useRegisterToolbar(toolbarState: ToolbarState, { skip = false }: { skip?: boolean } = {}): void {
  const ctx = useContext(ToolbarContext);
  if (!ctx) {
    throw new Error('useRegisterToolbar must be used within ToolbarProvider');
  }

  const { register, unregister } = ctx;

  // Всегда храним актуальное состояние в ref
  const latestRef = useRef(toolbarState);
  latestRef.current = toolbarState;

  // Синхронизируем при каждом рендере страницы.
  // Безопасно: register() обновляет ref и уведомляет подписчиков,
  // но НЕ вызывает setState провайдера → страница НЕ перерисовывается.
  useEffect(() => {
    if (!skip) {
      register(latestRef.current);
    }
  });

  // Очистка при размонтировании
  useEffect(() => {
    return () => unregister();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

/**
 * Регистрирует локальные действия таблицы (undo/redo/clear/changedCells)
 * в контексте тулбара по уникальному tableId.
 *
 * Поддерживает несколько таблиц на одной странице — каждая регистрируется
 * отдельно, а контекст объединяет все действия при чтении.
 */
export function useRegisterTableActions(tableId: string, actions: TableActionsState): void {
  const ctx = useContext(ToolbarContext);
  if (!ctx) {
    throw new Error('useRegisterTableActions must be used within ToolbarProvider');
  }

  const { registerTableActions, unregisterTableActions } = ctx;

  const latestActionsRef = useRef(actions);
  latestActionsRef.current = actions;

  const tableIdRef = useRef(tableId);
  tableIdRef.current = tableId;

  useEffect(() => {
    registerTableActions(tableIdRef.current, latestActionsRef.current);
  });

  useEffect(() => {
    const id = tableIdRef.current;
    return () => unregisterTableActions(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

/**
 * Подписывается на изменения toolbar-состояния через useSyncExternalStore.
 * Возвращает объединённое состояние (страница + действия всех таблиц).
 * Только компоненты, использующие этот хук, перерисовываются при изменениях.
 */
export function useToolbarState(): MergedToolbarState {
  const ctx = useContext(ToolbarContext);
  if (!ctx) {
    throw new Error('useToolbarState must be used within ToolbarProvider');
  }
  return useSyncExternalStore(ctx.subscribe, ctx.getSnapshot);
}

/**
 * Позволяет установить флаг «идёт перезагрузка» из любого компонента.
 * Контекст автоматически мёржит его с `loading` в объединённом состоянии.
 */
export function useSetReloading(): (value: boolean) => void {
  const ctx = useContext(ToolbarContext);
  if (!ctx) {
    throw new Error('useSetReloading must be used within ToolbarProvider');
  }
  return ctx.setReloading;
}

/**
 * Читает только флаг «идёт перезагрузка» без подписки на весь toolbar state.
 * Вызывает ре-рендер ТОЛЬКО при смене true ↔ false.
 * Безопасен вне ToolbarProvider — вернёт false.
 */
export function useIsReloading(): boolean {
  const ctx = useContext(ToolbarContext);
  const fallbackSubscribe = useCallback((cb: () => void) => { void cb; return () => {}; }, []);
  const fallbackSnapshot = useCallback(() => false, []);
  return useSyncExternalStore(
    ctx?.subscribe ?? fallbackSubscribe,
    ctx?.getReloadingSnapshot ?? fallbackSnapshot,
  );
}
